import { graphqlClient } from '@/graphql/graphql-client';
import { toScalarListVariables } from '@/graphql/list-query';
import { getCurrentOrgId } from '@/store/use-my-oganization-store';
import {
  DELETE_TEMPLATE_FIELD_MUTATION,
  DELETE_TEMPLATE_MUTATION,
  DELETE_TEMPLATE_SCREEN_MUTATION,
  INSERT_TEMPLATE_DOCX_MUTATION,
  INSERT_TEMPLATE_FIELD_MUTATION,
  INSERT_TEMPLATE_MUTATION,
  INSERT_TEMPLATE_REQUEST_MUTATION,
  INSERT_TEMPLATE_SCREEN_MUTATION,
  INSERT_TEMPLATE_VARIABLE_MUTATION,
  UPDATE_TEMPLATE_FIELD_MUTATION,
  UPDATE_TEMPLATE_MUTATION,
  UPDATE_TEMPLATE_SCREEN_MUTATION,
} from '@/graphql/mutations';
import {
  GET_TEMPLATE_FIELDS_QUERY,
  GET_TEMPLATE_SCREENS_QUERY,
  requestTemplateDocxs,
  requestTemplates,
  requestTemplateVariables,
  type TemplateDocxRecord,
  type TemplateRecord,
  type TemplateVariableRecord,
} from '@/graphql/queries';
import type {
  PaginationParams,
  Template,
  TemplateField,
  TemplateFieldInsertInput,
  TemplateFieldUpdateInput,
  TemplateInsertInput,
  TemplateScreen,
  TemplateScreenInsertInput,
  TemplateScreenUpdateInput,
  TemplateRequestInsertInput,
  TemplateUpdateInput,
  TemplateWithScreens,
  UdsTemplateField,
  UdsTemplateScreen,
  TemplateWithVariables,
  TemplateVariable,
  TemplateDocx,
  TemplateVariableInsertInput,
  TemplateDocxInsertInput,
} from '../types/templates.types';
import { slugify } from '@/lib/utils';

// ============================================================
// Helpers
// ============================================================


// ============================================================
// PascalCase → camelCase transforms (UDS → old Prisma shape)
// ============================================================

const strings = (values: Array<string | null> | null | undefined): string[] | undefined => {
  if (!values) return undefined;
  return values.filter((value): value is string => value != null);
};

const toTemplate = (t: TemplateRecord): Template => ({
  id: t.ItemId,
  createdAt: t.CreatedDate ?? undefined,
  isDeleted: t.IsDeleted ?? undefined,
  organizationIds: strings(t.OrganizationIds),
  slug: t.Slug,
  title: t.Title,
  keywords: strings(t.Keywords),
  description: t.Description ?? undefined,
  icon: t.Icon ?? undefined,
  available: t.Available ?? undefined,
  popular: t.Popular ?? undefined,
  href: t.Href ?? undefined,
  estimatedMinutes: t.EstimatedMinutes ?? undefined,
  systemPrompt: t.SystemPrompt ?? undefined,
  systemPromptRole: t.SystemPromptRole ?? undefined,
  uilmDescriptionKey: t.UilmDescriptionKey ?? undefined,
  uilmTitleKey: t.UilmTitleKey ?? undefined,
  previewToken: t.PreviewToken ?? undefined,
  category: t.Category ?? undefined,
  kind: t.Kind ?? undefined,
});

const toTemplateDocx = (d: TemplateDocxRecord): TemplateDocx => ({
  id: d.ItemId,
  organizationIds: strings(d.OrganizationIds),
  templateId: d.TemplateId ?? '',
  originalUrl: d.OriginalUrl ?? undefined,
  originalKey: d.OriginalKey ?? undefined,
  originalName: d.OriginalName ?? undefined,
  sizeBytes: d.SizeBytes ?? undefined,
  tokenizedUrl: d.TokenizedUrl ?? undefined,
  tokenizedKey: d.TokenizedKey ?? undefined,
  tokenVersion: d.TokenVersion ?? undefined,
  status: d.Status ?? undefined,
});

const toTemplateVariable = (v: TemplateVariableRecord): TemplateVariable => ({
  id: v.ItemId,
  createdAt: v.CreatedDate ?? undefined,
  organizationIds: strings(v.OrganizationIds),
  templateId: v.TemplateId ?? '',
  parentId: v.ParentId ?? null,
  token: v.Token ?? undefined,
  name: v.Name ?? undefined,
  label: v.Label ?? undefined,
  type: v.Type ?? undefined,
  required: v.Required ?? undefined,
  placeholder: v.Placeholder ?? undefined,
  helpText: v.HelpText ?? undefined,
  options: strings(v.Options),
  defaultValue: v.DefaultValue ?? undefined,
  format: v.Format ?? undefined,
  typeMeta: v.TypeMeta ?? undefined,
  order: v.Order ?? undefined,
  sourceSpan: v.SourceSpan ?? undefined,
  uilmLabelKey: v.UilmLabelKey ?? undefined,
  uilmPlaceholderKey: v.UilmPlaceholderKey ?? undefined,
  uilmHelpTextKey: v.UilmHelpTextKey ?? undefined,
  uilmOptionsKeys: strings(v.UilmOptionsKeys),
});

export const toScreen = (s: UdsTemplateScreen): TemplateScreen => ({
  id: s.ItemId,
  createdAt: s.CreatedDate,
  updatedAt: s.LastUpdatedDate,
  createdBy: s.CreatedBy,
  lastUpdatedBy: s.LastUpdatedBy,
  isDeleted: s.IsDeleted,
  organizationIds: s.OrganizationIds,
  tags: s.Tags,
  templateId: s.TemplateId,
  title: s.Title,
  description: s.Description,
  order: s.Order,
  type: s.Type,
  aiPrompt: s.AiPrompt,
  aiOutputSchema: s.AiOutputSchema,
  dynamicMaxFields: s.DynamicMaxFields,
  dynamicPrompt: s.DynamicPrompt,
  signatoryConfig: s.SignatoryConfig,
  uilmDescriptionKey: s.UilmDescriptionKey,
  uilmTitleKey: s.UilmTitleKey,
  enableApplyStandards: s.EnableApplyStandards,
  conditions: s.Conditions,
});

export const toField = (f: UdsTemplateField): TemplateField => ({
  id: f.ItemId,
  createdAt: f.CreatedDate,
  updatedAt: f.LastUpdatedDate,
  createdBy: f.CreatedBy,
  lastUpdatedBy: f.LastUpdatedBy,
  isDeleted: f.IsDeleted,
  organizationIds: f.OrganizationIds,
  tags: f.Tags,
  screenId: f.ScreenId,
  name: f.Name,
  label: f.Label,
  type: f.Type,
  required: f.Required,
  placeholder: f.Placeholder,
  helpText: f.HelpText,
  options: f.Options,
  order: f.Order,
  aiSuggestionEnabled: f.AiSuggestionEnabled,
  aiSuggestionKey: f.AiSuggestionKey,
  uilmHelpTextKey: f.UilmHelpTextKey,
  uilmLabelKey: f.UilmLabelKey,
  uilmOptionsKeys: f.UilmOptionsKeys,
  uilmPlaceholderKey: f.UilmPlaceholderKey,
  conditions: f.Conditions,
});

// ============================================================
// Template CRUD
// ============================================================

const emptyTemplatesResult = () => ({
  items: [] as TemplateRecord[],
  totalCount: 0,
  totalPages: 1,
  hasNextPage: false,
  hasPreviousPage: false,
});

export const getTemplatesRaw = async (params: PaginationParams) => {
  try {
    const response = await requestTemplates(params);
    return response?.getTemplatess ?? emptyTemplatesResult();
  } catch (error) {
    console.error('Error fetching templates:', error);
    return emptyTemplatesResult();
  }
};

export const getTemplates = async (params: PaginationParams) => {
  const result = await getTemplatesRaw(params);
  return {
    ...result,
    items: (result.items || []).map(toTemplate),
  };
};

export const createTemplate = async (input: TemplateInsertInput) => {
  const response = await graphqlClient.request(INSERT_TEMPLATE_MUTATION, input);
  return response;
};

export const updateTemplate = async (itemId: string, input: TemplateUpdateInput) => {
  const response = await graphqlClient.request(UPDATE_TEMPLATE_MUTATION, { ItemId: itemId, ...input });
  return response;
};

export const deleteTemplate = async (itemId: string, isHardDelete = false) => {
  const response = await graphqlClient.request(DELETE_TEMPLATE_MUTATION, {
    ItemId: itemId,
    isHardDelete,
  });
  return response;
};

// ============================================================
// TemplateScreen CRUD
// ============================================================

export const getTemplateScreens = async (params: PaginationParams) => {
  try {
    const response = await graphqlClient.query({
      query: GET_TEMPLATE_SCREENS_QUERY,
      variables: toScalarListVariables(params, { templateId: 'TemplateId' }),
    });
    return response?.getTemplateScreenss || { items: [], totalCount: 0 };
  } catch (error) {
    console.error('Error fetching template screens:', error);
    return { items: [], totalCount: 0 };
  }
};

export const getScreensByTemplateId = async (templateId: string) => {
  return getTemplateScreens({
    where: { TemplateId: { eq: templateId } },
    paging: { pageNo: 1, pageSize: 100 },
  });
};

export const createTemplateScreen = async (input: TemplateScreenInsertInput) => {
  const response = await graphqlClient.request(INSERT_TEMPLATE_SCREEN_MUTATION, input);
  return response;
};

export const updateTemplateScreen = async (itemId: string, input: TemplateScreenUpdateInput) => {
  const response = await graphqlClient.request(UPDATE_TEMPLATE_SCREEN_MUTATION, { ItemId: itemId, ...input });
  return response;
};

export const deleteTemplateScreen = async (itemId: string, isHardDelete = false) => {
  const response = await graphqlClient.request(DELETE_TEMPLATE_SCREEN_MUTATION, {
    ItemId: itemId,
    isHardDelete,
  });
  return response;
};

// ============================================================
// TemplateField CRUD
// ============================================================

export const getTemplateFields = async (params: PaginationParams) => {
  try {
    const response = await graphqlClient.query({
      query: GET_TEMPLATE_FIELDS_QUERY,
      variables: toScalarListVariables(params, { screenId: 'ScreenId' }),
    });
    return response?.getTemplateFieldss || { items: [], totalCount: 0 };
  } catch (error) {
    console.error('Error fetching template fields:', error);
    return { items: [], totalCount: 0 };
  }
};

export const getFieldsByScreenId = async (screenId: string) => {
  return getTemplateFields({
    where: { ScreenId: { eq: screenId } },
    paging: { pageNo: 1, pageSize: 100 },
  });
};

export const createTemplateField = async (input: TemplateFieldInsertInput) => {
  const response = await graphqlClient.request(INSERT_TEMPLATE_FIELD_MUTATION, input);
  return response;
};

export const updateTemplateField = async (itemId: string, input: TemplateFieldUpdateInput) => {
  const response = await graphqlClient.request(UPDATE_TEMPLATE_FIELD_MUTATION, { ItemId: itemId, ...input });
  return response;
};

export const deleteTemplateField = async (itemId: string, isHardDelete = false) => {
  const response = await graphqlClient.request(DELETE_TEMPLATE_FIELD_MUTATION, {
    ItemId: itemId,
    isHardDelete,
  });
  return response;
};

// ============================================================
// Composite: fetch public + org-specific templates
// Matches old Next.js GET /api/templates?orgId=xxx
// ============================================================

const availableWhere = (organizationId: string | null | undefined) => ({
  OrganizationId: { eq: organizationId ?? '' },
  Available: { eq: true },
});

const mergeTemplatesById = (
  groups: Array<{ items?: TemplateRecord[] | null } | null | undefined>
) => {
  const byId = new Map<string, TemplateRecord>();
  for (const group of groups) {
    for (const item of group?.items ?? []) {
      byId.set(item.ItemId, item);
    }
  }
  return [...byId.values()];
};

export const getAvailableTemplatesForOrg = async (orgId?: string | null) => {
  const resolvedOrgId = orgId ?? getCurrentOrgId();
  try {
    const publicRes = await requestTemplates({
      where: availableWhere(''),
      paging: { pageNo: 1, pageSize: 200 },
    });
    const orgRes = resolvedOrgId
      ? await requestTemplates({
          where: availableWhere(resolvedOrgId),
          paging: { pageNo: 1, pageSize: 200 },
        })
      : null;
    return mergeTemplatesById([publicRes?.getTemplatess, orgRes?.getTemplatess]).map(toTemplate);
  } catch (error) {
    console.error('Error fetching templates for org:', error);
    return [];
  }
};

// ============================================================
// Paginated available templates for org (server-side search/filter)
// ============================================================

export interface AvailableTemplatesParams {
  orgId?: string | null;
  pageNo: number;
  pageSize: number;
  search?: string;
  category?: string | null;
  /**
   * When true, return ONLY pure public templates (OrganizationId empty/null),
   * excluding the org's own available templates. Defaults to false, which
   * includes the org's own available templates alongside public ones.
   */
  publicOnly?: boolean;
}

export const getAvailableTemplatesForOrgPaginated = async (params: AvailableTemplatesParams) => {
  const { orgId, pageNo, pageSize, search, category, publicOnly } = params;
  const resolvedOrgId = orgId ?? getCurrentOrgId();

  try {
    const publicRes = await requestTemplates({
      where: availableWhere(''),
      paging: { pageNo: 1, pageSize: 200 },
    });
    const orgRes =
      resolvedOrgId && !publicOnly
        ? await requestTemplates({
            where: availableWhere(resolvedOrgId),
            paging: { pageNo: 1, pageSize: 200 },
          })
        : null;

    let items = mergeTemplatesById([publicRes?.getTemplatess, orgRes?.getTemplatess]).map(toTemplate);

    if (category) {
      items = items.filter((item) => item.category === category);
    }
    if (search?.trim()) {
      const query = search.trim().toLowerCase();
      items = items.filter(
        (item) =>
          item.title.toLowerCase().includes(query) ||
          (item.description ?? '').toLowerCase().includes(query)
      );
    }

    const totalCount = items.length;
    const start = (pageNo - 1) * pageSize;
    return {
      items: items.slice(start, start + pageSize),
      totalCount,
      totalPages: Math.ceil(totalCount / pageSize) || 1,
    };
  } catch (error) {
    console.error('Error fetching available templates for org:', error);
    return { items: [], totalCount: 0, totalPages: 1 };
  }
};
export const getOwnTemplatesForOrg = async (orgId?: string | null) => {
  try {
    const response = await requestTemplates({
      where: { OrganizationId: { eq: orgId ?? getCurrentOrgId() } },
      paging: { pageNo: 1, pageSize: 200 },
    });
    const items = response?.getTemplatess?.items || [];
    return items.map(toTemplate);
  } catch (error) {
    console.error('Error fetching templates for org:', error);
    return [];
  }
};

// ============================================================
// Paginated own templates for org (server-side search/pagination).
// Unlike getAvailableTemplatesForOrgPaginated this does NOT enforce
// { Available: true } — the org tab must show drafts too.
// ============================================================

export interface OwnTemplatesParams {
  orgId?: string | null;
  pageNo: number;
  pageSize: number;
  search?: string;
}

export const getOwnTemplatesForOrgPaginated = async (params: OwnTemplatesParams) => {
  const { orgId, pageNo, pageSize, search } = params;

  try {
    const response = await requestTemplates({
      where: { OrganizationId: { eq: orgId ?? getCurrentOrgId() } },
      paging: { pageNo: 1, pageSize: 200 },
    });
    let items = (response?.getTemplatess?.items || []).map(toTemplate);
    if (search?.trim()) {
      const query = search.trim().toLowerCase();
      items = items.filter(
        (item) => item.title.toLowerCase().includes(query) || item.slug.toLowerCase().includes(query)
      );
    }
    const totalCount = items.length;
    const start = (pageNo - 1) * pageSize;
    return {
      items: items.slice(start, start + pageSize),
      totalCount,
      totalPages: Math.ceil(totalCount / pageSize) || 1,
    };
  } catch (error) {
    console.error('Error fetching own templates for org:', error);
    return { items: [], totalCount: 0, totalPages: 1 };
  }
};

// ============================================================
// Slug availability check
// ============================================================

export const checkSlugAvailability = async (slug: string, excludeId?: string): Promise<boolean> => {
  try {
    const result = await getTemplates({
      where: { Slug: { eq: slug } },
      paging: { pageNo: 1, pageSize: 1 },
    });
    const items = result.items || [];
    if (excludeId) {
      const remaining = items.filter((i) => i.id !== excludeId);
      return remaining.length === 0;
    }
    return items.length === 0;
  } catch {
    return false;
  }
};

// ============================================================
// Preview token management
// ============================================================

export const generatePreviewToken = async (templateId: string) => {
  const token = crypto.randomUUID();
  await updateTemplate(templateId, { PreviewToken: token });
  return token;
};

export const revokePreviewToken = async (templateId: string) => {
  await updateTemplate(templateId, { PreviewToken: '' });
};

// ============================================================
// Get template by ID with screens & fields
// ============================================================

/**
 * One template row, and nothing else.
 *
 * `getTemplateByIdWithScreensAndFields` costs at least two SERIAL round trips (template, then
 * screens, then a fan-out for fields) — and a fixed-DOCX template has no screens or fields at
 * all, so for the DOCX builder every request after the first is pure latency on the critical
 * path, in front of a blocking loader.
 */
export const getTemplateBasicById = async (templateId: string): Promise<Template | null> => {
  const result = await getTemplatesRaw({
    where: { ItemId: { eq: templateId } },
    paging: { pageNo: 1, pageSize: 1 },
  });
  const raw = result.items || [];
  return raw.length > 0 ? toTemplate(raw[0]) : null;
};

export const getTemplateByIdWithScreensAndFields = async (
  templateId: string
): Promise<TemplateWithScreens | null> => {
  const templatesResult = await getTemplatesRaw({
    where: { ItemId: { eq: templateId } },
    paging: { pageNo: 1, pageSize: 1 },
  });
  const rawTemplates = templatesResult.items || [];
  if (rawTemplates.length === 0) return null;

  const template = toTemplate(rawTemplates[0]);

  const screensResult = await getScreensByTemplateId(rawTemplates[0].ItemId);
  const rawScreens: UdsTemplateScreen[] = screensResult.items || [];

  if (rawScreens.length === 0) {
    return { ...template, screens: [] };
  }

  const fieldResults = await Promise.all(rawScreens.map((s) => getFieldsByScreenId(s.ItemId)));

  const screens = rawScreens
    .map((rawScreen, idx) => {
      const rawFields: UdsTemplateField[] = fieldResults[idx].items || [];
      return {
        ...toScreen(rawScreen),
        fields: rawFields.map(toField).sort((a, b) => a.order - b.order),
      };
    })
    .sort((a, b) => a.order - b.order);

  return { ...template, screens };
};

// ============================================================
// Clone (deep copy) a template
// ============================================================

/** Phases of a clone, reported via `onStep` so the UI can show progress. */
export type CloneStep = 'preparing' | 'creating' | 'copying' | 'done';

export interface CloneTemplateArgs {
  /** Source template header (id, slug, kind, metadata) from the list row. */
  source: Template;
  /** New title chosen by the user for the copy. */
  newTitle: string;
  /** Org that will own the cloned template. */
  orgId: string;
  /** Optional progress callback fired as each phase begins/completes. */
  onStep?: (step: CloneStep) => void;
}

export interface CloneTemplateResult {
  id: string;
  slug: string;
  kind?: string;
}

/**
 * Generates a slug that is not yet taken, starting from `slugify(base)` and
 * appending `-copy`, `-copy-2`, … until `checkSlugAvailability` reports free.
 */
const generateUniqueCloneSlug = async (base: string): Promise<string> => {
  const root = slugify(base) || 'template';
  // Prefer a clean "-copy" suffix, then numbered variants.
  const candidates = [
    `${root}-copy`,
    ...Array.from({ length: 50 }, (_, i) => `${root}-copy-${i + 2}`),
  ];
  for (const candidate of candidates) {
    if (await checkSlugAvailability(candidate)) return candidate;
  }
  // Extreme fallback — unique by construction.
  return `${root}-copy-${crypto.randomUUID().slice(0, 8)}`;
};

/**
 * Deep-clones a template into a new DRAFT owned by `orgId`.
 *
 * There is no server-side duplicate mutation, so this orchestrates chained
 * inserts client-side. It is NOT transactional — a mid-way failure can leave
 * a partial template; callers should surface the error clearly.
 */
export const cloneTemplate = async ({
  source,
  newTitle,
  orgId,
  onStep,
}: CloneTemplateArgs): Promise<CloneTemplateResult> => {
  const isDocx = source.kind === 'fixed_docx';

  onStep?.('preparing');
  const slug = await generateUniqueCloneSlug(newTitle);

  // 1. Create the new template header (copy metadata, override identity/ownership/status).
  const headerInput: TemplateInsertInput = {
    OrganizationId: orgId,
    Slug: slug,
    Title: newTitle,
    Keywords: source.keywords,
    Description: source.description,
    Icon: source.icon,
    Available: false,
    Popular: false,
    Href: isDocx ? `/templates/${slug}/generate-docx` : `/templates/${slug}/generate`,
    EstimatedMinutes: source.estimatedMinutes,
    SystemPrompt: source.systemPrompt,
    SystemPromptRole: source.systemPromptRole,
    UilmDescriptionKey: source.uilmDescriptionKey,
    UilmTitleKey: source.uilmTitleKey,
    Category: source.category,
    Kind: source.kind,
  };

  onStep?.('creating');
  const headerRes = await createTemplate(headerInput);
  const newId = headerRes?.insertTemplates?.itemId;
  if (!newId) {
    throw new Error('Failed to create cloned template');
  }

  // 2. Deep-copy children onto the new template id.
  onStep?.('copying');
  if (isDocx) {
    await cloneDocxChildren(source.id, newId);
  } else {
    await cloneScreensAndFields(source.id, newId);
  }

  onStep?.('done');
  return { id: newId, slug, kind: source.kind };
};

/** Copies every screen (and its fields) from `sourceId` onto `newTemplateId`. */
const cloneScreensAndFields = async (sourceId: string, newTemplateId: string): Promise<void> => {
  const full = await getTemplateByIdWithScreensAndFields(sourceId);
  const screens = full?.screens ?? [];

  for (const screen of screens) {
    const screenInput: TemplateScreenInsertInput = {
      TemplateId: newTemplateId,
      Title: screen.title,
      Description: screen.description,
      Order: screen.order,
      Type: screen.type,
      AiPrompt: screen.aiPrompt,
      AiOutputSchema: screen.aiOutputSchema,
      DynamicMaxFields: screen.dynamicMaxFields,
      DynamicPrompt: screen.dynamicPrompt,
      SignatoryConfig: screen.signatoryConfig,
      UilmDescriptionKey: screen.uilmDescriptionKey,
      UilmTitleKey: screen.uilmTitleKey,
      EnableApplyStandards: screen.enableApplyStandards,
      Conditions: screen.conditions,
    };

    const screenRes = await createTemplateScreen(screenInput);
    const newScreenId = screenRes?.insertTemplateScreens?.itemId;
    if (!newScreenId) continue;

    for (const field of screen.fields) {
      const fieldInput: TemplateFieldInsertInput = {
        ScreenId: newScreenId,
        Name: field.name,
        Label: field.label,
        Type: field.type,
        Required: field.required,
        Placeholder: field.placeholder,
        HelpText: field.helpText,
        Options: field.options,
        Order: field.order,
        AiSuggestionEnabled: field.aiSuggestionEnabled,
        AiSuggestionKey: field.aiSuggestionKey,
        UilmHelpTextKey: field.uilmHelpTextKey,
        UilmLabelKey: field.uilmLabelKey,
        UilmOptionsKeys: field.uilmOptionsKeys,
        UilmPlaceholderKey: field.uilmPlaceholderKey,
        Conditions: field.conditions,
      };
      await createTemplateField(fieldInput);
    }
  }
};

/**
 * Copies the docx file record and the variable tree from `sourceId` onto
 * `newTemplateId`. The underlying storage file is shared (same URLs/keys) —
 * no re-upload. Variables preserve their ParentId tree via an old→new id map.
 */
const cloneDocxChildren = async (sourceId: string, newTemplateId: string): Promise<void> => {
  const [variableResponse, docxResponse] = await Promise.all([
    requestTemplateVariables({
      where: { TemplateId: { eq: sourceId } },
      paging: { pageNo: 1, pageSize: 200 },
    }),
    requestTemplateDocxs({
      where: { TemplateId: { eq: sourceId } },
      paging: { pageNo: 1, pageSize: 10 },
    }),
  ]);

  const variables = variableResponse.getTemplateVariabless?.items ?? [];
  const docxs = docxResponse.getTemplateDocxs?.items ?? [];
  const docx = docxs[0];
  if (docx) {
    const docxInput: TemplateDocxInsertInput = {
      TemplateId: newTemplateId,
      OriginalUrl: docx.OriginalUrl ?? undefined,
      OriginalKey: docx.OriginalKey ?? undefined,
      OriginalName: docx.OriginalName ?? undefined,
      SizeBytes: docx.SizeBytes ?? undefined,
      TokenizedUrl: docx.TokenizedUrl ?? undefined,
      TokenizedKey: docx.TokenizedKey ?? undefined,
      TokenVersion: docx.TokenVersion ?? undefined,
      Status: docx.Status ?? undefined,
    };
    await graphqlClient.request(INSERT_TEMPLATE_DOCX_MUTATION, docxInput);
  }

  const createTemplateVariable = async (input: TemplateVariableInsertInput) => {
    const response = await graphqlClient.request(INSERT_TEMPLATE_VARIABLE_MUTATION, input);
    return response.insertTemplateVariables?.itemId ?? '';
  };

  const idMap = new Map<string, string>();
  const remaining = [...variables];
  let progressed = true;
  while (remaining.length > 0 && progressed) {
    progressed = false;
    for (let i = remaining.length - 1; i >= 0; i--) {
      const variable = remaining[i];
      const parentId = variable.ParentId;
      if (parentId && !idMap.has(parentId)) continue;
      const input: TemplateVariableInsertInput = {
        TemplateId: newTemplateId,
        ParentId: parentId ? idMap.get(parentId) : undefined,
        Token: variable.Token ?? undefined,
        Name: variable.Name ?? undefined,
        Label: variable.Label ?? undefined,
        Type: variable.Type ?? undefined,
        Required: variable.Required ?? undefined,
        Placeholder: variable.Placeholder ?? undefined,
        HelpText: variable.HelpText ?? undefined,
        Options: strings(variable.Options),
        DefaultValue: variable.DefaultValue ?? undefined,
        Format: variable.Format ?? undefined,
        TypeMeta: variable.TypeMeta ?? undefined,
        Order: variable.Order ?? undefined,
        SourceSpan: variable.SourceSpan ?? undefined,
        UilmLabelKey: variable.UilmLabelKey ?? undefined,
        UilmPlaceholderKey: variable.UilmPlaceholderKey ?? undefined,
        UilmHelpTextKey: variable.UilmHelpTextKey ?? undefined,
        UilmOptionsKeys: strings(variable.UilmOptionsKeys),
      };
      const newVarId = await createTemplateVariable(input);
      if (newVarId) idMap.set(variable.ItemId, newVarId);
      remaining.splice(i, 1);
      progressed = true;
    }
  }
  for (const orphan of remaining) {
    await createTemplateVariable({
      TemplateId: newTemplateId,
      ParentId: undefined,
      Token: orphan.Token ?? undefined,
      Name: orphan.Name ?? undefined,
      Label: orphan.Label ?? undefined,
      Type: orphan.Type ?? undefined,
      Required: orphan.Required ?? undefined,
      Placeholder: orphan.Placeholder ?? undefined,
      HelpText: orphan.HelpText ?? undefined,
      Options: strings(orphan.Options),
      DefaultValue: orphan.DefaultValue ?? undefined,
      Format: orphan.Format ?? undefined,
      TypeMeta: orphan.TypeMeta ?? undefined,
      Order: orphan.Order ?? undefined,
      SourceSpan: orphan.SourceSpan ?? undefined,
      UilmLabelKey: orphan.UilmLabelKey ?? undefined,
      UilmPlaceholderKey: orphan.UilmPlaceholderKey ?? undefined,
      UilmHelpTextKey: orphan.UilmHelpTextKey ?? undefined,
      UilmOptionsKeys: strings(orphan.UilmOptionsKeys),
    });
  }
};

// ============================================================
// Composite: fetch template by slug with screens & fields
// Returns camelCase shape matching old Prisma includes
// ============================================================

export const getTemplateBySlugWithScreensAndFields = async (
  slug: string
): Promise<TemplateWithScreens | null> => {
  // 1. Fetch the template by slug (raw PascalCase)
  const templatesResult = await getTemplatesRaw({
    where: { Slug: { eq: slug } },
    paging: { pageNo: 1, pageSize: 1 },
  });
  const rawTemplates = templatesResult.items || [];
  if (rawTemplates.length === 0) return null;

  const template = toTemplate(rawTemplates[0]);

  // 2. Fetch all screens for this template
  const screensResult = await getScreensByTemplateId(rawTemplates[0].ItemId);
  const rawScreens: UdsTemplateScreen[] = screensResult.items || [];

  if (rawScreens.length === 0) {
    return { ...template, screens: [] };
  }

  // 3. Fetch fields for all screens in parallel
  const fieldResults = await Promise.all(rawScreens.map((s) => getFieldsByScreenId(s.ItemId)));

  // 4. Build screens with nested fields, sorted by order
  const screens = rawScreens
    .map((rawScreen, idx) => {
      const rawFields: UdsTemplateField[] = fieldResults[idx].items || [];
      return {
        ...toScreen(rawScreen),
        fields: rawFields.map(toField).sort((a, b) => a.order - b.order),
      };
    })
    .sort((a, b) => a.order - b.order);

  return { ...template, screens };
};

export const getTemplateBySlugWithVariablesAndDocx = async (
  slug: string
): Promise<TemplateWithVariables | null> => {
  // 1. Fetch the template by slug (raw PascalCase)
  const templatesResult = await getTemplatesRaw({
    where: { Slug: { eq: slug } },
    paging: { pageNo: 1, pageSize: 1 },
  });
  const rawTemplates = templatesResult.items || [];
  if (rawTemplates.length === 0) return null;
  const template = toTemplate(rawTemplates[0]);

  // 2. Fetch all variables for this template
  const variableResponse = await requestTemplateVariables({
    where: { TemplateId: { eq: rawTemplates[0].ItemId } },
    paging: { pageNo: 1, pageSize: 200 },
  });
  const variables = (variableResponse.getTemplateVariabless?.items ?? []).map(toTemplateVariable);

  if (variables.length === 0) {
    return { ...template, variables: [], docx: null };
  }

  const docxResponse = await requestTemplateDocxs({
    where: { TemplateId: { eq: rawTemplates[0].ItemId } },
    paging: { pageNo: 1, pageSize: 10 },
  });
  const rawDocxs = (docxResponse.getTemplateDocxs?.items ?? []).map(toTemplateDocx);

  const docx = rawDocxs.length > 0 ? rawDocxs[0] : null;

  return { ...template, variables, docx };
};

// ============================================================
// Template Requests
// ============================================================

export const createTemplateRequest = async (input: TemplateRequestInsertInput) => {
  const response = await graphqlClient.request(INSERT_TEMPLATE_REQUEST_MUTATION, {
    ...input,
    Status: input.Status ?? 'pending',
  });
  return response;
};
